import { useMemo } from "react"

import { Button, Select } from "@productlane/ui"

interface ChangelogColumnStepProps {
	data: string[][]
	hasHeaders: boolean
	back: () => void
	next: () => void
	titleIndex: number
	setTitleIndex: (i: number) => void
	descriptionIndex: number
	setDescriptionIndex: (i: number) => void
	imageIndex: number
	setImageIndex: (i: number) => void
	sortedData: string[][]
}

function stripHtml(html: string): string {
	// Remove HTML tags
	const stripped = html.replace(/<[^>]*>/g, "")
	// Decode common HTML entities
	const decoded = stripped
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
	return decoded.trim()
}

export function ChangelogColumnStep({
	data,
	hasHeaders,
	back,
	next,
	titleIndex,
	setTitleIndex,
	descriptionIndex,
	setDescriptionIndex,
	imageIndex,
	setImageIndex,
	sortedData,
}: ChangelogColumnStepProps) {
	const headers = useMemo(() => {
		if (!data[0]) {
			return []
		}
		const options = hasHeaders
			? data[0].map((it, i) => ({
					label: it,
					value: i.toString(),
				}))
			: data[0].map((_, i) => ({
					label: `${i + 1}. Column`,
					value: i.toString(),
				}))

		return [{ label: "None", value: "-1" }, ...options]
	}, [hasHeaders, data])

	return (
		<div className="space-y-4">
			<Select
				value={titleIndex.toString()}
				name="titleIndex"
				label="Select the column with the title (required)"
				options={headers.filter((h) => h.value !== "-1")}
				onChange={(it) => setTitleIndex(Number(it))}
				className="px-[1px]"
			/>

			<Select
				value={descriptionIndex.toString()}
				name="descriptionIndex"
				label="Select the column with the content (optional)"
				options={headers}
				onChange={(it) => setDescriptionIndex(Number(it))}
				className="px-[1px]"
			/>

			<Select
				value={imageIndex.toString()}
				name="imageIndex"
				label="Select the column with the image URL (optional)"
				options={headers}
				onChange={(it) => setImageIndex(Number(it))}
				className="px-[1px]"
			/>

			<div className="mb-2 mt-4 text-sm text-primary">Preview:</div>
			<div className="relative mt-1 overflow-hidden rounded-lg border bg-white p-2 dark:bg-gray-900">
				{sortedData[titleIndex]
					?.slice(hasHeaders ? 1 : 0, hasHeaders ? 5 : 4)
					.map((it, i) => {
						const rawDescription =
							sortedData[descriptionIndex]?.[hasHeaders ? i + 1 : i]
						const cleanDescription = rawDescription
							? stripHtml(rawDescription)
							: ""

						return (
							<div className="border-b p-1 text-secondary" key={i}>
								<div className="flex items-center gap-2 text-sm text-primary">
									Title: <div className="text-secondary">{it}</div>
								</div>
								{imageIndex !== -1 && (
									<div className="mt-1 flex gap-2 truncate text-sm text-primary">
										Image:
										<div className="truncate text-secondary">
											{sortedData[imageIndex]?.[hasHeaders ? i + 1 : i] ||
												"Not specified"}
										</div>
									</div>
								)}
								{descriptionIndex !== -1 && cleanDescription && (
									<div className="mt-1 text-sm text-primary">
										Description:{" "}
										<span className="line-clamp-2 text-secondary">
											{cleanDescription}
										</span>
									</div>
								)}
							</div>
						)
					})}
				<div className="absolute inset-x-0 bottom-0 h-[80%] bg-gradient-to-b from-transparent via-white/80 to-white dark:via-brand-900/80 dark:to-brand-900" />
			</div>

			<div className="mt-2 flex justify-between">
				<Button variant="ghost" onClick={back}>
					Back
				</Button>
				<Button onClick={next}>Next</Button>
			</div>
		</div>
	)
}
